import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Edit, Trash2, Save, Loader2, List, Scale,
  Check, X, AlertTriangle, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { extrajudicialAPI } from '../api';
import { toast } from '../hooks/use-toast';

const GRUPOS_CLAUSULA = [
  { id: 'gastos', nombre: 'Gastos Hipotecarios', color: 'bg-blue-100 text-blue-800' },
  { id: 'nulidad', nombre: 'Nulidad (sin importe)', color: 'bg-purple-100 text-purple-800' },
  { id: 'otros', nombre: 'Otros', color: 'bg-gray-100 text-gray-800' }
];

const ConfiguracionCatalogoClausulas = () => {
  const navigate = useNavigate();
  const [clausulas, setClausulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClausula, setEditingClausula] = useState(null);
  const [saving, setSaving] = useState(false);
  const [incluirInactivas, setIncluirInactivas] = useState(false);
  
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    grupo: 'gastos',
    porcentaje_reclamable: 100,
    tiene_importe: true,
    orden: 0,
    activa: true
  });

  const loadClausulas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await extrajudicialAPI.getCatalogoClausulas(incluirInactivas);
      setClausulas(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error', description: 'No se pudo cargar el catálogo', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [incluirInactivas]);

  useEffect(() => {
    loadClausulas();
  }, [loadClausulas]);

  const handleOpenDialog = (clausula = null) => {
    if (clausula) {
      setEditingClausula(clausula);
      setFormData({
        codigo: clausula.codigo,
        nombre: clausula.nombre,
        descripcion: clausula.descripcion || '',
        grupo: clausula.grupo,
        porcentaje_reclamable: clausula.porcentaje_reclamable,
        tiene_importe: clausula.tiene_importe,
        orden: clausula.orden,
        activa: clausula.activa
      });
    } else {
      setEditingClausula(null);
      setFormData({
        codigo: '',
        nombre: '',
        descripcion: '',
        grupo: 'gastos',
        porcentaje_reclamable: 100,
        tiene_importe: true,
        orden: clausulas.length,
        activa: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.codigo || !formData.nombre) {
      toast({ title: 'Error', description: 'Código y nombre son obligatorios', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (editingClausula) {
        await extrajudicialAPI.actualizarClausulaCatalogo(editingClausula.id, formData);
        toast({ title: 'Cláusula actualizada' });
      } else {
        await extrajudicialAPI.crearClausulaCatalogo(formData);
        toast({ title: 'Cláusula creada' });
      }
      setIsDialogOpen(false);
      loadClausulas();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'No se pudo guardar',
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clausula) => {
    if (!window.confirm(`¿Desactivar la cláusula "${clausula.nombre}"? Podrá reactivarla más tarde.`)) {
      return;
    }

    try {
      await extrajudicialAPI.eliminarClausulaCatalogo(clausula.id);
      toast({ title: 'Cláusula desactivada' });
      loadClausulas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleReactivar = async (clausula) => {
    try {
      await extrajudicialAPI.actualizarClausulaCatalogo(clausula.id, {
        ...clausula,
        activa: true
      });
      toast({ title: 'Cláusula reactivada' });
      loadClausulas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const getGrupoInfo = (grupoId) => {
    return GRUPOS_CLAUSULA.find(g => g.id === grupoId) || GRUPOS_CLAUSULA[2];
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/configuracion')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Scale className="w-6 h-6" />
              Catálogo de Cláusulas Abusivas
            </h1>
            <p className="text-sm text-gray-500">
              Gestiona las cláusulas disponibles en el módulo de Reclamación Extrajudicial
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadClausulas}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Cláusula
          </Button>
        </div>
      </div>

      {/* Filtro */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={incluirInactivas}
                onCheckedChange={setIncluirInactivas}
                id="incluir-inactivas"
              />
              <Label htmlFor="incluir-inactivas" className="text-sm">
                Mostrar cláusulas desactivadas
              </Label>
            </div>
            <Badge variant="outline">{clausulas.length} cláusulas</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de cláusulas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <List className="w-5 h-5" />
            Cláusulas Configuradas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : clausulas.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <List className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay cláusulas configuradas</p>
              <Button variant="outline" className="mt-4" onClick={() => handleOpenDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera cláusula
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Orden</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead className="text-center">% Reclamable</TableHead>
                  <TableHead className="text-center">Con Importe</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="w-24">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clausulas.map((clausula) => {
                  const grupoInfo = getGrupoInfo(clausula.grupo);
                  return (
                    <TableRow key={clausula.id} className={!clausula.activa ? 'opacity-50' : ''}>
                      <TableCell className="font-mono text-gray-500">{clausula.orden}</TableCell>
                      <TableCell className="font-mono text-xs">{clausula.codigo}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{clausula.nombre}</p>
                          {clausula.descripcion && (
                            <p className="text-xs text-gray-500 truncate max-w-xs">{clausula.descripcion}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={grupoInfo.color}>{grupoInfo.nombre}</Badge>
                      </TableCell>
                      <TableCell className="text-center">{clausula.porcentaje_reclamable}%</TableCell>
                      <TableCell className="text-center">
                        {clausula.tiene_importe ? (
                          <Check className="w-4 h-4 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-gray-400 mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {clausula.activa ? (
                          <Badge className="bg-green-100 text-green-700">Activa</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Inactiva</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(clausula)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {clausula.activa ? (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(clausula)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => handleReactivar(clausula)}>
                              <RefreshCw className="w-4 h-4 text-green-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog para crear/editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingClausula ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingClausula ? 'Editar Cláusula' : 'Nueva Cláusula'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({...formData, codigo: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                  placeholder="clausula_ejemplo"
                  disabled={!!editingClausula}
                />
                <p className="text-xs text-gray-500 mt-1">Identificador único (sin espacios)</p>
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={formData.orden}
                  onChange={(e) => setFormData({...formData, orden: parseInt(e.target.value) || 0})}
                  min={0}
                />
              </div>
            </div>

            <div>
              <Label>Nombre <span className="text-red-500">*</span></Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                placeholder="Nombre de la cláusula"
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                placeholder="Descripción detallada de la cláusula..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Grupo</Label>
                <Select value={formData.grupo} onValueChange={(v) => setFormData({...formData, grupo: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GRUPOS_CLAUSULA.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>% Reclamable</Label>
                <Input
                  type="number"
                  value={formData.porcentaje_reclamable}
                  onChange={(e) => setFormData({...formData, porcentaje_reclamable: parseFloat(e.target.value) || 100})}
                  min={0}
                  max={100}
                  step={0.1}
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.tiene_importe}
                  onCheckedChange={(c) => setFormData({...formData, tiene_importe: c})}
                  id="tiene-importe"
                />
                <Label htmlFor="tiene-importe" className="text-sm">Tiene importe económico</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.activa}
                  onCheckedChange={(c) => setFormData({...formData, activa: c})}
                  id="activa"
                />
                <Label htmlFor="activa" className="text-sm">Activa</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionCatalogoClausulas;
